/**
 * Audio Feature Mixin
 *
 * Provides convenience getters for common audio features.
 * These are shared across all visualizer base classes to avoid duplication.
 *
 * Usage: Object.assign(BaseClass.prototype, AudioFeatureMixin);
 *
 * Requires the class to have a `_currentFeatures` property that holds
 * the current frame's AudioFeatures object (or null).
 */

export const AudioFeatureMixin = {
  /**
   * Returns true if a beat/onset was detected this frame
   * @returns {boolean}
   */
  get beatDetected() {
    return this._currentFeatures?.rhythm?.isOnset || false;
  },

  /**
   * Returns the beat/onset confidence (0-1)
   * @returns {number}
   */
  get beatConfidence() {
    return this._currentFeatures?.rhythm?.confidence || 0;
  },

  /**
   * Returns the spectral brightness/centroid (0-1, higher = brighter)
   * @returns {number}
   */
  get spectralBrightness() {
    return this._currentFeatures?.spectral?.centroid || 0.5;
  },

  /**
   * Returns the spectral flux (rate of change, 0-1)
   * @returns {number}
   */
  get spectralFlux() {
    return this._currentFeatures?.spectral?.flux || 0;
  },

  /**
   * Returns the sub-bass energy (20-60Hz, 0-1)
   * @returns {number}
   */
  get subBassEnergy() {
    return this._currentFeatures?.bands?.subBass || 0;
  },

  /**
   * Returns the bass energy (60-250Hz, 0-1)
   * @returns {number}
   */
  get bassEnergy() {
    return this._currentFeatures?.bands?.bass || 0;
  },

  /**
   * Returns the mid-range energy (500-2000Hz, 0-1)
   * @returns {number}
   */
  get midEnergy() {
    return this._currentFeatures?.bands?.mid || 0;
  },

  /**
   * Returns the high/brilliance energy (6000-20000Hz, 0-1)
   * @returns {number}
   */
  get highEnergy() {
    return this._currentFeatures?.bands?.brilliance || 0;
  }
};

/**
 * Apply the audio feature mixin to a class prototype.
 * Uses Object.defineProperties to properly transfer getters.
 * @param {Function} TargetClass - The class to extend
 */
export function applyAudioFeatureMixin(TargetClass) {
  const descriptors = Object.getOwnPropertyDescriptors(AudioFeatureMixin);
  Object.defineProperties(TargetClass.prototype, descriptors);
}
